"use client";

import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";

import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { Switch } from "@/components/shadcn/switch";
import { BindableInput } from "./BindableInput";
import { cn } from "@/lib/utils";

/**
 * V2 renderer for `display=ConfigBindableBool`. Literal mode is a True/False
 * select, or a switch with `widget="switch"`;
 * variable mode shows a chip when the stored value is exactly `:var[<ref>]`. The picker filters to bool-typed sources. Shares the bind
 * affordance + chip semantics with the other bindable fields via
 * {@link BindableInput} (bool `dataType`).
 */
export function BindableBoolField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  placeholder,
  isRequired,
  showOptionalLabel,
  error,
  defaultValue,
  widget = "select",
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
  defaultValue?: boolean;
  widget?: "switch" | "select";
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path);
  const value: string =
    typeof raw === "string" ? raw : typeof raw === "boolean" ? String(raw) : "";
  const resolvedLabel = useMemo(
    () => label || humanize(fieldKey),
    [label, fieldKey]
  );
  const invalid = !!error;
  const setValue = (next: string | boolean) =>
    form.setValue(path, toStoredBool(next, defaultValue), {
      shouldDirty: true,
      shouldTouch: true,
    });
  const checked = value === "" ? defaultValue === true : value === "true";
  const shownValue =
    value === "" && defaultValue !== undefined ? String(defaultValue) : value;

  return (
    <div className="flex flex-col gap-1">
      <NoxusFormLabel
        label={resolvedLabel}
        tooltip={tooltip}
        name={path}
        noMargin
        isDisabled={disabled}
        isRequired={isRequired}
        showOptionalLabel={showOptionalLabel}
      />
      <div
        className={cn(
          invalid && "rounded-md ring-1 ring-inset ring-warning-400/70"
        )}
      >
        <BindableInput
          nodeId={nodeId}
          dataType="bool"
          inputName={fieldKey}
          value={shownValue}
          disabled={disabled}
          placeholder={placeholder}
          onChange={setValue}
          renderLiteral={
            widget === "select" ? undefined : (
              <div className="flex h-8 items-center">
                <Switch
                  id={path}
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={setValue}
                />
              </div>
            )
          }
        />
      </div>
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? (
        <NoxusAlert look="banner" title={error} variant="warning" size="xs" />
      ) : null}
    </div>
  );
}

// A cleared variable stores the default (the backend rejects "" for a bool);
// the select's "true"/"false" are saved as booleans, like the switch.
function toStoredBool(
  next: string | boolean,
  defaultValue: boolean | undefined
): string | boolean {
  if (next === "") return defaultValue === true;
  if (next === "true") return true;
  if (next === "false") return false;
  return next;
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
